import useSQLite from '../../useSQLite'
import { defineSqliteFeature } from '../define-feature'

const { dbRun, dbQuery, dbExecute } = useSQLite()

let schemaReady = false
let schemaPromise = null
let dbLock = Promise.resolve()

const withDbLock = async (fn) => {
    const task = dbLock.then(fn, fn)
    dbLock = task.catch(() => {})
    return task
}

const RESUME_TABLE = 'reinforce_download_resume'
const CHUNKS_TABLE = 'reinforce_download_chunks'
const nowSeconds = () => Math.floor(Date.now() / 1000)

const ensureSchema = async () => {
    if (schemaReady) return
    if (schemaPromise) return schemaPromise

    schemaPromise = (async () => {
        try {
            await dbExecute(`
                CREATE TABLE IF NOT EXISTS ${RESUME_TABLE} (
                    filename TEXT PRIMARY KEY,
                    offset INTEGER NOT NULL DEFAULT 0,
                    pos_token TEXT,
                    total_size INTEGER NOT NULL DEFAULT 0,
                    created_at INTEGER NOT NULL DEFAULT (strftime('%s','now')),
                    updated_at INTEGER NOT NULL DEFAULT (strftime('%s','now'))
                );
                CREATE TABLE IF NOT EXISTS ${CHUNKS_TABLE} (
                    filename TEXT NOT NULL,
                    offset INTEGER NOT NULL,
                    data TEXT NOT NULL,
                    created_at INTEGER NOT NULL DEFAULT (strftime('%s','now')),
                    updated_at INTEGER NOT NULL DEFAULT (strftime('%s','now')),
                    PRIMARY KEY (filename, offset)
                );
                CREATE INDEX IF NOT EXISTS idx_${CHUNKS_TABLE}_file ON ${CHUNKS_TABLE}(filename);
            `)
            schemaReady = true
        } catch (error) {
            console.warn('[ReinforceDownload] Failed to ensure schema', error)
            throw error
        }
    })()

    try {
        await schemaPromise
    } finally {
        schemaPromise = null
    }
}

const toBase64 = (bytes) => {
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}

const fromBase64 = (value) => {
    if (!value) return new Uint8Array(0)
    const binary = atob(value)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i)
    }
    return out
}

export const readResumeState = async (filename) => {
    if (!filename) return null
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const result = await dbQuery(
                `SELECT offset, pos_token, total_size, updated_at FROM ${RESUME_TABLE} WHERE filename = ? LIMIT 1;`,
                [filename]
            )
            const row = result?.values?.[0]
            if (!row) return null
            return {
                offset: Number(row.offset) || 0,
                posToken: row.pos_token || '',
                totalSize: Number(row.total_size) || 0,
                updatedAt: Number(row.updated_at) || 0,
            }
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to read resume state', filename, error)
        return null
    }
}

export const writeResumeState = async (filename, { offset = 0, posToken = '', totalSize = 0 } = {}) => {
    if (!filename) return false
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            await dbRun(
                `INSERT INTO ${RESUME_TABLE}(filename, offset, pos_token, total_size, created_at, updated_at)
                 VALUES(?, ?, ?, ?, strftime('%s','now'), strftime('%s','now'))
                 ON CONFLICT(filename) DO UPDATE SET
                    offset = excluded.offset,
                    pos_token = excluded.pos_token,
                    total_size = excluded.total_size,
                    updated_at = excluded.updated_at,
                    created_at = COALESCE(created_at, excluded.created_at);`,
                [filename, Math.max(0, Math.floor(offset || 0)), posToken || '', Math.max(0, Math.floor(totalSize || 0))]
            )
            return true
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to write resume state', filename, error)
        return false
    }
}

export const clearResumeState = async (filename) => {
    if (!filename) return false
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            await dbRun(`DELETE FROM ${RESUME_TABLE} WHERE filename = ?;`, [filename])
            return true
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to clear resume state', filename, error)
        return false
    }
}

export const listResumeFiles = async () => {
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const result = await dbQuery(`SELECT filename FROM ${RESUME_TABLE} ORDER BY filename;`)
            return result?.values?.map((row) => row.filename).filter(Boolean) ?? []
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to list resume entries', error)
        return []
    }
}

export const writeChunkBatch = async (filename, chunks = []) => {
    if (!filename || !Array.isArray(chunks) || chunks.length === 0) return false
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            for (const chunk of chunks) {
                const offset = Math.max(0, Math.floor(chunk?.offset || 0))
                const bytes = chunk?.bytes instanceof Uint8Array ? chunk.bytes : null
                if (!bytes || bytes.length === 0) continue
                const encoded = toBase64(bytes)
                await dbRun(
                    `INSERT INTO ${CHUNKS_TABLE}(filename, offset, data, created_at, updated_at)
                     VALUES(?, ?, ?, strftime('%s','now'), strftime('%s','now'))
                     ON CONFLICT(filename, offset) DO UPDATE SET
                        data = excluded.data,
                        updated_at = excluded.updated_at,
                        created_at = COALESCE(created_at, excluded.created_at);`,
                    [filename, offset, encoded]
                )
            }
            return true
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to write chunks', filename, error)
        return false
    }
}

export const readChunks = async (filename) => {
    if (!filename) return []
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const result = await dbQuery(
                `SELECT offset, data FROM ${CHUNKS_TABLE} WHERE filename = ? ORDER BY offset;`,
                [filename]
            )
            if (!result?.values?.length) return []
            return result.values
                .map((row) => ({
                    offset: Number(row.offset) || 0,
                    bytes: fromBase64(row.data || ''),
                }))
                .filter((row) => row.bytes && row.bytes.length > 0)
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to read chunks', filename, error)
        return []
    }
}

export const clearChunks = async (filename) => {
    if (!filename) return false
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            await dbRun(`DELETE FROM ${CHUNKS_TABLE} WHERE filename = ?;`, [filename])
            return true
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to clear chunks', filename, error)
        return false
    }
}

export const listChunkFiles = async () => {
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const result = await dbQuery(`SELECT DISTINCT filename FROM ${CHUNKS_TABLE} ORDER BY filename;`)
            return result?.values?.map((row) => row.filename).filter(Boolean) ?? []
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to list chunk files', error)
        return []
    }
}

export const clearAllDownloadCache = async () => {
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            await dbRun(`DELETE FROM ${RESUME_TABLE};`)
            await dbRun(`DELETE FROM ${CHUNKS_TABLE};`)
            return true
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to clear download cache', error)
        return false
    }
}

export const getDownloadCacheSummary = async () => {
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const resumeResult = await dbQuery(`SELECT COUNT(*) as count FROM ${RESUME_TABLE};`)
            const chunksResult = await dbQuery(
                `SELECT COUNT(*) as count, SUM(LENGTH(data)) as bytes FROM ${CHUNKS_TABLE};`
            )
            const resumeCount = Number(resumeResult?.values?.[0]?.count) || 0
            const chunksCount = Number(chunksResult?.values?.[0]?.count) || 0
            const chunksBase64Bytes = Number(chunksResult?.values?.[0]?.bytes) || 0
            const chunksApproxBytes = Math.floor(chunksBase64Bytes * 0.75)
            return {
                resumeCount,
                chunksCount,
                chunksBase64Bytes,
                chunksApproxBytes,
            }
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to summarize download cache', error)
        return {
            resumeCount: 0,
            chunksCount: 0,
            chunksBase64Bytes: 0,
            chunksApproxBytes: 0,
        }
    }
}

export const pruneDownloadCache = async ({ maxAgeSec } = {}) => {
    const ttl = Number(maxAgeSec)
    if (!Number.isFinite(ttl) || ttl <= 0) return { ok: false, resumeCleared: 0, chunksCleared: 0 }
    try {
        return await withDbLock(async () => {
            await ensureSchema()
            const cutoff = nowSeconds() - Math.floor(ttl)
            const resumeCountResult = await dbQuery(
                `SELECT COUNT(*) as count FROM ${RESUME_TABLE} WHERE updated_at < ?;`,
                [cutoff]
            )
            const chunksCountResult = await dbQuery(
                `SELECT COUNT(*) as count FROM ${CHUNKS_TABLE} WHERE updated_at < ?;`,
                [cutoff]
            )
            const resumeCleared = Number(resumeCountResult?.values?.[0]?.count) || 0
            const chunksCleared = Number(chunksCountResult?.values?.[0]?.count) || 0
            if (resumeCleared > 0) {
                await dbRun(`DELETE FROM ${RESUME_TABLE} WHERE updated_at < ?;`, [cutoff])
            }
            if (chunksCleared > 0) {
                await dbRun(`DELETE FROM ${CHUNKS_TABLE} WHERE updated_at < ?;`, [cutoff])
            }
            return { ok: true, resumeCleared, chunksCleared }
        })
    } catch (error) {
        console.warn('[ReinforceDownload] Failed to prune download cache', error)
        return { ok: false, resumeCleared: 0, chunksCleared: 0 }
    }
}

const REINFORCE_DOWNLOAD_TTL_SEC = 3 * 86400
const NOOP = async () => {}

export default defineSqliteFeature({
    key: 'reinforceDownload',
    purgeOnLogout: true,
    ensureSchema: NOOP,
    onBoot: () => pruneDownloadCache({ maxAgeSec: REINFORCE_DOWNLOAD_TTL_SEC }),
    getSummary: () => getDownloadCacheSummary(),
    purge: async () => {
        const before = await getDownloadCacheSummary()
        const ok = await clearAllDownloadCache()
        return {
            ok: Boolean(ok),
            resumeCleared: ok ? Number(before?.resumeCount) || 0 : 0,
            chunksCleared: ok ? Number(before?.chunksCount) || 0 : 0,
        }
    },
    fallbacks: {
        onBoot: { ok: false, resumeCleared: 0, chunksCleared: 0 },
        getSummary: null,
        purge: { ok: false, resumeCleared: 0, chunksCleared: 0 },
    },
})
